import React from "react";
import { FiSearch, FiClock, FiXCircle } from "react-icons/fi";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import type { DeploymentQueueItem } from "../../API/Models";
import { DEFAULT_LANGUAGE } from "./deploymentUtils";

const DeploymentQueueList: React.FC<{
	items: DeploymentQueueItem[];
	loading: boolean;
	error: string | null;
	onLoad?: () => void;
	onViewChanges?: (item: DeploymentQueueItem) => void;
}> = ({ items, loading, error, onLoad, onViewChanges }) => (
	<div className="space-y-3">
		{loading && (
			<div className="flex items-center gap-2 text-slate-500 text-sm">
				<LoadingSpinner size="sm" /> Loading deployment queue…
			</div>
		)}
		{error && (
			<div className="rounded-lg border border-rose-200 bg-rose-50/80 px-3 py-2 text-sm text-rose-700">
				<p>{error}</p>
				{onLoad && (
					<button
						type="button"
						onClick={onLoad}
						className="mt-2 inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
					>
						Retry
					</button>
				)}
			</div>
		)}
		{!loading && !error && items.length === 0 && (
			<div className="rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-sm text-emerald-700">
				No pending deployment queue items.
			</div>
		)}
		{items.map((item) => {
			const language = item.language ?? DEFAULT_LANGUAGE;
			const contentType = item.contentType ?? "content";
			const slug = item.slug ?? item.contentId ?? "";
			const action = (item.action ?? "updated").toLowerCase();
			const status = (item.status ?? "pending").toLowerCase();
			const message = item.message ?? "";
			const itemError = item.error ?? "";
			const pagePaths = Array.isArray(item.pagePaths) ? item.pagePaths.filter(Boolean) : [];
			const pathsPreview =
				pagePaths.length > 0
					? pagePaths.slice(0, 3).join(", ") + (pagePaths.length > 3 ? ` (+${pagePaths.length - 3} more)` : "")
					: "";

			const actionStyle = action === "deleted"
				? "border-rose-200 bg-rose-50 text-rose-700"
				: action === "added" || action === "created"
				? "border-emerald-200 bg-emerald-50 text-emerald-700"
				: "border-sky-200 bg-sky-50 text-sky-700";

			const statusStyle = status === "failed"
				? "border-rose-200 bg-rose-50 text-rose-700"
				: status === "processing"
				? "border-amber-200 bg-amber-50 text-amber-700"
				: "border-slate-200 bg-slate-50 text-slate-600";

			const statusIcon = status === "failed"
				? <FiXCircle className="h-3 w-3" />
				: status === "processing"
				? <FiClock className="h-3 w-3" />
				: null;

			const borderStyle = status === "failed" ? "border-rose-200" : "border-slate-200";

			return (
				<div key={item.id ?? `${contentType}-${language}-${slug}`} className={`rounded-xl border ${borderStyle} bg-white p-3 text-sm`}>
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div className="min-w-0 flex-1">
							<p className="font-semibold text-slate-900">
								{contentType}
								{slug ? `/${slug}` : ""}{" "}
								<span className="text-xs font-medium text-slate-400">({language})</span>
							</p>
							<div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
								<span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${actionStyle}`}>
									{action}
								</span>
								<span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${statusStyle}`}>
									{statusIcon}{status}
								</span>
								{item.author && (
									<span className="text-slate-500">by {item.author}</span>
								)}
							</div>
							{message && <p className="mt-1 text-xs text-slate-600">{message}</p>}
							{itemError && (
								<div className="mt-2 rounded-lg border border-rose-200 bg-rose-50/80 px-2 py-1.5 text-xs text-rose-700">
									<span className="font-medium">Error:</span> {itemError}
								</div>
							)}
							{pathsPreview && (
								<p className="mt-2 text-xs text-slate-500">
									Page paths: {pathsPreview}
								</p>
							)}
						</div>
						{onViewChanges && (
							<button
								type="button"
								onClick={() => onViewChanges(item)}
								className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
								title="View changes (diff)"
							>
								<FiSearch className="h-3 w-3" /> View changes
							</button>
						)}
					</div>
				</div>
			);
		})}
	</div>
);

export default DeploymentQueueList;
